/**
 * The live admin frame (admin-design-r1, navigation A with the palette): the library AppShell drawn from
 * src/sections.ts, with the nav badges, every founder in the palette, the mock banner, and the box drawer that any
 * page opens with `?open=box:<slug>`. Badges cost one polled call, `GET /api/admin/counts`.
 */
import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { countsMissing, fallbackCounts } from '../domain/counts'
import type { AdminCounts } from '../domain/schemas'
import { sections } from '../sections'
import { visibleInterval } from '../infra/visibleInterval'
import { AppShell, useNav, type Command } from '../ui'
import { useApi } from './api-context'
import { BoxDrawer } from './components/BoxDiagnosis'
import { useSession } from './session'
import { useLoad } from './useLoad'

/** How often the nav badges refresh while the tab is visible. */
export const COUNTS_POLL_MS = 30_000

/**
 * The registry's `count` keys from one call, `GET /api/admin/counts`, polled while the tab is visible. An API from
 * before that route answers 404: then the alerts and waitlist badges come from their own calls, as before, and Runs
 * shows none.
 */
export function useCounts(): Partial<AdminCounts> {
  const { api } = useApi()
  const counts = useLoad(() => api.counts(), [api])
  const missing = countsMissing(counts.error)
  const alerts = useLoad(() => (missing ? api.alerts({ status: 'open', limit: 1 }) : Promise.resolve(null)), [api, missing])
  const waitlist = useLoad(() => (missing ? api.waitlist() : Promise.resolve(null)), [api, missing])
  const reload = useRef(() => {})
  reload.current = () => (missing ? (alerts.reload(), waitlist.reload()) : counts.reload())
  useEffect(() => visibleInterval(() => reload.current(), COUNTS_POLL_MS), [])
  return useMemo(() => (missing ? fallbackCounts(alerts.data, waitlist.data) : (counts.data ?? {})), [missing, counts.data, alerts.data, waitlist.data])
}

export function AdminShell({ children }: { children: ReactNode }) {
  const s = useSession()
  const { api } = useApi()
  const { navigate } = useNav()
  const counts = useCounts()
  const founders = useLoad(() => api.founders(), [api])
  const commands = useMemo<Command[]>(
    () => (founders.data ?? []).map((f) => ({ id: `founder:${f.id}`, label: f.name, group: 'Founders', icon: 'founders', hint: f.slug, keywords: f.slug, run: () => navigate(`/founders/${f.id}`) })),
    [founders.data, navigate],
  )
  useEffect(() => {
    document.title = 'cpg.computer · Admin'
  }, [])
  return (
    <AppShell
      sections={sections}
      counts={counts}
      user={s.email ? { email: s.email } : undefined}
      onSignOut={s.isAuthenticated ? s.logout : undefined}
      commands={commands}
      showDev={import.meta.env.DEV}
      banner={s.mock ? 'Mock · Example data' : undefined}
    >
      {children}
      <BoxDrawer />
    </AppShell>
  )
}
